import { compose, filter, get, isEmpty, map, negate, size, some } from 'lodash/fp';
import { createSelector } from 'reselect';
import { flow, pipe } from 'fp-ts/function';
import { array } from 'fp-ts';
import { getOrEmptyObject, getStringProp, gtOne, keyById } from '@mews/fp';
import { composeProps, createAction, createPropsExtender, DurationUtils, Selector, TimeService } from '@mews/framework';
import { TimeUnit } from '@mews/enums';
import { ApplicationState, Option, WithEnterpriseId } from '@mews-commander/types';
import { BookableServiceData, Service } from '@mews-commander/data-access';
import { Identifiable } from '@mews/std';
import { getContainer } from '@mews-commander/legacy-app-instance';
import {
    mapId,
    notNil,
    createOptionsSelector,
    getData,
    isDayUse,
    isHourUse,
    isMonthUse,
    isNightUse,
} from '@mews-commander/utils';
import { orderOrderableEntities } from '../utils/orderableEntities';
import prefix from '../prefix';
import parentSelector from '../selector';
import { isAdditionalService, isBookableService, serviceToOption } from './servicesNew';
import { createBaseEntitySelectors, createEntitiesReducer, localizedEntities } from './utils';

export const NAME = 'services';

const SERVICES_UPDATED = prefix('SERVICES_UPDATED');
const SERVICES_DELETED = prefix('SERVICES_DELETED');

export const [getServices, getServicesByIds, getServicesByOwnerId, getService] = createBaseEntitySelectors<
    ApplicationState,
    Service,
    typeof NAME
>(parentSelector, NAME);

export const setServices = (services: Service[]) => createAction(SERVICES_UPDATED, services);
export const deleteServices = (ids: string[]) => createAction(SERVICES_DELETED, ids);

export const composeServiceIdFromProps =
    (selector: $TSFixMe) =>
    (state: $TSFixMe, { serviceId }: $TSFixMe) =>
        selector(state, { id: serviceId });

export const servicesReducer = createEntitiesReducer({
    UPDATE_SUCCESS: SERVICES_UPDATED,
    DELETE_SUCCESS: SERVICES_DELETED,
});

export const getServiceDefaultBusinessSegmentId = compose(get('defaultBusinessSegmentId'), getService);

export const getServiceActiveState = compose(get('isActive'), getService);
export const getServiceActiveStateByServiceId = composeServiceIdFromProps(getServiceActiveState);

const getActiveServices = createSelector(getServices, filter<Service>({ isActive: true }));
export const getEnterpriseActiveServices = createSelector(
    getServices,
    getStringProp('enterpriseId'),
    (services, enterpriseId) => filter({ isActive: true, enterpriseId }, services)
);

export const getProductServices = compose(filter(isAdditionalService), getActiveServices);

export const getProductServicesOptions = flow(getProductServices, array.map(serviceToOption));

export const getEnterpriseProductServices = createSelector(
    getProductServices,
    getStringProp('enterpriseId'),
    (productServices, enterpriseId) => filter({ enterpriseId }, productServices)
);

export const getBookableServices = createSelector(getActiveServices, compose(keyById(), filter(isBookableService)));

export const getEnterpriseBookableServices = createSelector(
    getBookableServices,
    getStringProp('enterpriseId'),
    // @ts-expect-error $TSFixMeError: No overload matches this call.
    (bookableServices, enterpriseId) => filter({ enterpriseId }, bookableServices)
);

export const getAllEnterpriseServiceIds = createSelector(getActiveServices, mapId);

// @ts-expect-error $TSFixMeError: No overload matches this call.
export const getEnterpriseBookableServiceIds = createSelector(getEnterpriseBookableServices, mapId);

export const hasMultipleBookableServices = compose(gtOne, size, getEnterpriseBookableServices);

export const hasEnterpriseBookableServices = compose(negate(isEmpty), getEnterpriseBookableServices);

const getServiceOptions = compose(getOrEmptyObject('options'), getService);

export const getServiceEnterpriseId = createSelector(getService, (service) => service?.enterpriseId as string);

export const getEnterpriseIdFromServiceId = composeProps(
    getServiceEnterpriseId,
    createPropsExtender('id', getStringProp('serviceId'))
);

export const getServiceArrivalTime = createSelector(getService, get('arrivalTime'));
const getServiceDepartureTime = createSelector(getService, get('departureTime'));

export const isArrivalTimeAfterDepartureTime = createSelector(
    getServiceArrivalTime,
    getServiceDepartureTime,
    (arrivalTime, departureTime) => {
        if (!arrivalTime || !departureTime) return;
        const parsedArrivalTime = TimeService.parse(arrivalTime);
        const parsedDepartureTime = TimeService.parse(departureTime);
        return TimeService.isGreater(parsedArrivalTime, parsedDepartureTime);
    }
);

const getVisitOptions = (data: BookableServiceData) => data?.visitOptions || {};

export const getServiceData = createSelector(getService, getData);

const getServiceVisitOptions = createSelector(getServiceData, getVisitOptions);

export const getServiceOption = (option: string) => compose(get(option), getVisitOptions, getData);

const createServiceVisitOptionSelector = (optionName: $TSFixMe) => compose(get(optionName), getServiceVisitOptions);

export const [
    getApplyCancellationFeeByDefaultVisitOption,
    getSendCancellationEmailByDefaultVisitOption,
    getSendConfirmationEmailByDefault,
    getCloseBalancedBillsAtCheckOut,
    getRequirePreauthorizationForStart,
    getEnableSkipCleaning,
] = map(createServiceVisitOptionSelector, [
    'applyCancellationFeeByDefault',
    'sendCancellationEmailByDefault',
    'sendConfirmationEmailByDefault',
    'closeBalancedBillsAtCheckOut',
    'requirePreauthorizationForStart',
    'enableSkipCleaning',
]);

const createServiceOptionSelector = (optionName: $TSFixMe) => compose(get(optionName), getServiceOptions);

export const [
    isServiceBusinessSegmentRequired,
    getHasConfigurableBusinessSegment,
    doesServiceOrderRequireCompletedNotes,
    doesServiceHaveOverridablePrice,
    doesServiceHaveOverridableTax,
] = map(createServiceOptionSelector, [
    'orderRequiresSegment',
    'hasConfigurableBusinessSegment',
    'orderRequiresCompletedNotes',
    'hasOverridablePrice',
    'hasOverridableTax',
]);

export const getServiceTimeUnit: (state: ApplicationState, params: Identifiable) => TimeUnit | undefined =
    createSelector(getServiceData, get('timeUnit'));
export const getTimeUnitByServiceId = composeServiceIdFromProps(getServiceTimeUnit);
export const getServiceEndOffset = createSelector(getServiceData, get('endOffset'));
export const getServiceStartOffset = createSelector(getServiceData, get('startOffset'));

export const getIsOverlappingNextTimeUnit = createSelector(
    getServiceEndOffset,
    DurationUtils.isOverlappingNextTimeUnit
);

export const getIsOverlappingNextTimeUnitByServiceId = composeServiceIdFromProps(
    createSelector(getServiceEndOffset, DurationUtils.isOverlappingNextTimeUnit)
);

const getAllDayUseServices = createSelector(getBookableServices, filter(isDayUse));
export const getAllNightUseServices = createSelector(getBookableServices, filter(isNightUse));
const getAllMonthUseServices = createSelector(getBookableServices, filter(isMonthUse));
const getAllHourUseServices = createSelector(getBookableServices, filter(isHourUse));

export const getNightServices = createSelector(getEnterpriseBookableServices, filter(isNightUse));

export const hasEnterpriseNightServices = compose(negate(isEmpty), getNightServices);

export const isEnterpriseCityTaxReportEnabled = createSelector(
    getEnterpriseBookableServices,
    some(({ cityTaxCode }) => notNil(cityTaxCode))
);

export const getAllDayUseServiceIds = createSelector(getAllDayUseServices, mapId);
export const getAllNightUseServiceIds = createSelector(getAllNightUseServices, mapId);
export const getAllMonthUseServiceIds = createSelector(getAllMonthUseServices, mapId);
export const getAllHourUseServiceIds = createSelector(getAllHourUseServices, mapId);
export const getAllServiceIdsSelectableBySingleDay = createSelector(
    getAllDayUseServiceIds,
    getAllHourUseServiceIds,
    (dayServiceIds, hourServiceIds) => pipe(dayServiceIds, array.concat(hourServiceIds))
);

export const isDayUseService = createSelector(getService, (service) => !!service && isDayUse(service));
export const isNightUseService = createSelector(getService, (service) => !!service && isNightUse(service));
export const isMonthUseService = createSelector(getService, (service) => !!service && isMonthUse(service));

export const getServiceNames = createSelector(getService, get('names'));

export const serviceCreateOptionsProps = {
    label: 'localizedName',
    sortFunction: orderOrderableEntities,
};

const createServiceOptions = (servicesSelector: $TSFixMe) =>
    createOptionsSelector(localizedEntities(servicesSelector, 'names', 'localizedName'), serviceCreateOptionsProps);

export const getEnterpriseServiceOptions: Selector<ApplicationState, WithEnterpriseId, Option[]> =
    createServiceOptions(getEnterpriseBookableServices);

export const getEnterpriseNightServiceOptions: Selector<ApplicationState, WithEnterpriseId, Option[]> =
    createServiceOptions(getNightServices);

export const getBookableServicesByIds = compose(filter(isBookableService), getServicesByIds);

export const getLocalizedServiceName = createSelector(getServiceNames, getContainer, (names, container) => {
    const { appLocalizationComponent } = container();

    return appLocalizationComponent.localizeProperty(names);
});

export const getEnterpriseBookableServiceOptions: Selector<ApplicationState, WithEnterpriseId, Option[]> =
    createServiceOptions(getEnterpriseBookableServices);
